import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';

const COLORS = ['#6B4EE6', '#FF7B3B', '#FFD12E', '#36A2EB', '#4BC0C0', '#9966FF'];

const FeedbackDashboard = ({ feedbacks, onRefresh }) => {
  // Helpers to aggregate data
  const countAnswers = (key) => {
    const counts = {};
    feedbacks.forEach(f => {
      const val = f[key] || 'Unanswered';
      counts[val] = (counts[val] || 0) + 1;
    });
    return Object.keys(counts).map(k => ({ name: k, value: counts[k] })).sort((a, b) => b.value - a.value);
  };

  const announcementData = useMemo(() => countAnswers('announcement_source'), [feedbacks]);
  const missAnnouncementsData = useMemo(() => countAnswers('miss_announcements'), [feedbacks]);
  const teammateSourceData = useMemo(() => countAnswers('teammate_source'), [feedbacks]);
  const formattingData = useMemo(() => countAnswers('needed_feature'), [feedbacks]);
  const helpfulnessData = useMemo(() => {
    // Sort helpfulness by stars properly
    const stars = ["★☆☆☆☆", "★★☆☆☆", "★★★☆☆", "★★★★☆", "★★★★★"];
    const aggregated = countAnswers('feature_helpfulness');
    return aggregated.sort((a, b) => stars.indexOf(a.name) - stars.indexOf(b.name));
  }, [feedbacks]);

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ color: '#333', margin: 0 }}>Feedback Insights Dashboard</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ background: '#f0f0f5', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold', color: '#6B4EE6' }}>
            {feedbacks.length} Responses
          </span>
          <button className="btn-profile btn-profile-secondary" onClick={onRefresh}>Refresh Data</button>
        </div>
      </div>

      {feedbacks.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#888', background: '#fafafa', borderRadius: '8px' }}>
          No feedback responses available yet. Run the survey to see insights.
        </div>
      ) : (
        <>
          {/* Charts Section */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '24px', marginBottom: '32px' }}>
            
            {/* Chart 1 */}
            <div style={{ background: '#fafafa', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
              <h3 style={{ fontSize: '15px', color: '#444', marginBottom: '16px', textAlign: 'center' }}>How do you receive announcements?</h3>
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={announcementData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                      {announcementData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip wrapperStyle={{ borderRadius: '8px', zIndex: 100 }} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 2 */}
            <div style={{ background: '#fafafa', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
              <h3 style={{ fontSize: '15px', color: '#444', marginBottom: '16px', textAlign: 'center' }}>Frequency of missing announcements</h3>
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={missAnnouncementsData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis dataKey="name" tick={{fontSize: 12}} />
                    <YAxis allowDecimals={false} />
                    <Tooltip cursor={{fill: '#f0f0f0'}} wrapperStyle={{ borderRadius: '8px' }} />
                    <Bar dataKey="value" fill="#FF7B3B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 3 */}
            <div style={{ background: '#fafafa', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
              <h3 style={{ fontSize: '15px', color: '#444', marginBottom: '16px', textAlign: 'center' }}>How do you find teammates?</h3>
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={teammateSourceData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({name, percent}) => `${(percent * 100).toFixed(0)}%`}>
                      {teammateSourceData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />)}
                    </Pie>
                    <Tooltip wrapperStyle={{ borderRadius: '8px' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 4 */}
            <div style={{ background: '#fafafa', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
              <h3 style={{ fontSize: '15px', color: '#444', marginBottom: '16px', textAlign: 'center' }}>Most requested connectivity feature</h3>
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formattingData} layout="vertical" margin={{ top: 0, right: 20, left: 40, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="name" type="category" width={140} tick={{fontSize: 12}} />
                    <Tooltip cursor={{fill: '#f0f0f0'}} />
                    <Bar dataKey="value" fill="#6B4EE6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Chart 5 */}
            <div style={{ background: '#fafafa', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0', gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: '15px', color: '#444', marginBottom: '16px', textAlign: 'center' }}>Predicted Helpfulness rating of new features</h3>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={helpfulnessData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{fontSize: 16, fill: '#FFD12E'}} />
                    <YAxis allowDecimals={false} />
                    <Tooltip cursor={{fill: '#f0f0f0'}} />
                    <Bar dataKey="value" fill="#FFD12E" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '32px 0' }} />

          {/* Table Details */}
          <h3 style={{ fontSize: '18px', color: '#333', marginBottom: '16px' }}>Detailed Responses</h3>
          <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #eee' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd', color: '#555', backgroundColor: '#fcfcfc' }}>
                  <th style={{ padding: '14px', whiteSpace: 'nowrap' }}>Student</th>
                  <th style={{ padding: '14px', whiteSpace: 'nowrap' }}>Announcements</th>
                  <th style={{ padding: '14px', whiteSpace: 'nowrap' }}>Missed info?</th>
                  <th style={{ padding: '14px' }}>Biggest issue</th>
                  <th style={{ padding: '14px', whiteSpace: 'nowrap' }}>Teammates</th>
                  <th style={{ padding: '14px' }}>Attendance</th>
                  <th style={{ padding: '14px' }}>#1 Needed Feature</th>
                  <th style={{ padding: '14px' }}>Helpfulness</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((f, i) => (
                  <tr key={f.id || i} style={{ borderBottom: '1px solid #f0f0f0', color: '#333' }}>
                    <td style={{ padding: '12px 14px', minWidth: '160px' }}>
                      <strong style={{display: 'block', fontSize: '14px'}}>{f.user_name || 'Anonymous'}</strong>
                      <span style={{color: '#777'}}>{f.user_department || '-'} / {f.year || '-'}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>{f.announcement_source || '-'}</td>
                    <td style={{ padding: '12px 14px' }}>{f.miss_announcements || '-'}</td>
                    <td style={{ padding: '12px 14px', minWidth: '200px' }}>{f.communication_issue || '-'}</td>
                    <td style={{ padding: '12px 14px' }}>{f.teammate_source || '-'}</td>
                    <td style={{ padding: '12px 14px' }}>{f.attendance_method || '-'}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 'bold' }}>{f.needed_feature || '-'}</td>
                    <td style={{ padding: '12px 14px', color: '#FFD12E', fontSize: '16px' }}>{f.feature_helpfulness || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default FeedbackDashboard;
